import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactSVG from "react-svg";
import cx from "classnames";
import Loader from "../Loader";
import SmartPortal from "../SmartPortal";
import "./styles.scss";

const isValidProtocol = url => {
    const protocols = ["http://", "https://", "ftp://"];
    const lowerUrl = url.toLowerCase();
    return protocols.some(protocol => lowerUrl.startsWith(protocol));
};

const parseUrlLabel = input => {
    if (typeof input !== "string" || !input.trim()) {
        return [null, null];
    }

    const trimmedInput = input.trim();

    const markdownLinkRegex = /^\[(.*?)\]\((.*?)\)$/;
    const match = trimmedInput.match(markdownLinkRegex);

    if (match) {
        let label = match[1].trim();
        let url = match[2].trim();

        if (isValidProtocol(url)) {
            return [url, label];
        }

        return [null, null];
    }

    if (isValidProtocol(trimmedInput)) {
        if (/\s/.test(trimmedInput)) {
            return [null, null];
        }

        return [trimmedInput, trimmedInput];
    }

    return [null, null];
};

const Reference = props => {
    const {
        title,
        required,
        disabled,
        input,
        meta,
        t,
        liveSave,
        onLiveSave,
        portalPlacement = "bottom inner-left",
        repositionTrigger,
        delayedRepositionTrigger,
        delayedRepositionDelay = 500,
        isCollapsed,
        // initialValue,
    } = props;

    const placeholder = disabled ? "" : t("add");

    const currentValue = useMemo(() => {
        return input && input.value;
    }, [input]);

    const [url, label] = useMemo(() => {
        return parseUrlLabel(input.value);
    });

    const [isEditing, setIsEditing] = useState(false);
    const [urlModal, setUrlModal] = useState(url);
    const [labelModal, setLabelModal] = useState(label);
    const [urlModalError, setUrlModalError] = useState(null);
    const [initValue, setInitValue] = useState(currentValue);
    const [isSaving, setIsSaving] = useState(false);
    const [isWaitToBeLiveSaved, setIsWaitToBeLiveSaved] = useState(false);

    const widgetRef = useRef(null);

    const handleEditClick = () => {
        const toggledEditing = !isEditing;
        setIsEditing(toggledEditing);
        if (toggledEditing) {
            input.onFocus();
        } else {
            undoChanges();
        }
    };

    const tryToLiveSave = () => {
        // due to non sync redux form update on input.onChange
        // we need to wait until the next render cycle to be sure that value was actually changed
        if (onLiveSave && typeof onLiveSave === "function") {
            // setIsWaitToBeLiveSaved(true);
            setTimeout(() => setIsWaitToBeLiveSaved(true), 0);
        } else {
            console.error("Reference::tryToLiveSave: onLiveSave function is not provided");
        }
        return;
    };

    // this is the "single source" of changing the values, we receiving a value and processing everything else based on it
    // so it could be either
    // new value from 'handleSave'
    // empty value from 'handleDelete'
    // initial value (initValue state) from 'undoChanges' (this one is with omitLiveSave: true)
    // omitInputValue change is used by undoChanges (we do not need to change input value in such case)
    const handleValueChange = (value, omitLiveSave, omitInputValueChange) => {
        if (!omitInputValueChange) {
            input.onChange(value);
        }
        const [newUrl, newLabel] = parseUrlLabel(value);
        setLabelModal(newLabel);
        setUrlModal(newUrl);
        setUrlModalError(null);
        // omitLiveSave is provided on undoChanges (so we will not trigger handleLiveSave on 'click outside')
        if (!omitLiveSave && liveSave) {
            // try to live save value
            input.onBlur();
            input.onFocus();
            tryToLiveSave();
        } else {
            setInitValue(value);
            handleClose();
        }
    };

    const handleDelete = () => {
        handleValueChange("");
    };

    const handleSave = () => {
        if (!urlModal || urlModal.trim() === "") {
            setUrlModalError(t("validation.required"));
            return;
        }

        const urlRegex = /^(https?|ftp):\/\/[^\s\/$.?#].[^\s]*$/;
        if (!urlRegex.test(urlModal.trim())) {
            setUrlModalError(t("reference.invalidUrl"));
            return;
        }

        const newValue = labelModal
            ? `[${labelModal.trim()}](${urlModal.trim()})`
            : `[${urlModal.trim()}](${urlModal.trim()})`;

        handleValueChange(newValue);
    };

    const handleClose = () => {
        setIsEditing(false);
        input.onBlur();
    };

    const handleLiveSave = () => {
        if (isSaving) {
            return;
        }
        setIsWaitToBeLiveSaved(false);
        setIsSaving(true);
        const onFinish = isSuccess => {
            setIsSaving(false);

            if (isSuccess) {
                setUrlModalError(null);
                setInitValue(currentValue);
                handleClose();
            }
            if (!isSuccess) {
                input.onChange(initValue);
            }
            // since we are submitting a required empty fields (with validation UI error)
            // we do not need that logic anymore

            // old logic
            // we need that logic to handle specific case
            // when ui didn't sent a request (saving failed because of validation error on UI side)
            // in such case we should check for NO ERROR inside of meta
            // if there is NO ERROR we should reset input value to initValue
            // if (!isSuccess && !meta.error) {
            //     input.onChange(initValue);
            // }
            // if (!isSuccess && meta.error) {
            //     // there was probably a deletion of required field
            //     // set initValue to current value and
            //     setIsEditing(false);
            //     setInitValue(currentValue);
            // }
        };
        onLiveSave(input.name, onFinish);
    };

    // fallback to initValue and close modal
    const undoChanges = () => {
        // if we are saving - we do not need to "reset" values
        // maybe we also need to disable the input or show some loader inside of input as well
        // in order to inform user that the value is still saving (event if the modal was closed by the clicking outside)
        if (isSaving) {
            // is currently saving - just close the widget
            handleClose();
        } else {
            // is not saving - fallback to init value
            // omitting live save
            // omitting value of the input change
            handleValueChange(initValue, true, true);
        }
    };

    useEffect(() => {
        if (isCollapsed) {
            undoChanges();
        }
    }, [isCollapsed]);

    useEffect(() => {
        if (isWaitToBeLiveSaved) {
            handleLiveSave();
        }
    }, [isWaitToBeLiveSaved, currentValue]);

    useEffect(() => {
        if (input && input.value) {
            const [url, label] = parseUrlLabel(input.value);
            if (url === null && label === null) {
                handleValueChange("");
            }
        }
    }, []);

    return (
        // used the same data-portal-key as in the SmartPortal
        // in order to omit the onClickOutside event trigger on handleEditClick
        // instead only handleEditClick will triggered which cause the reference widget to be closed by click on "edit icon"
        <div className="input-element reference-field" data-portal-key={input.name} ref={widgetRef}>
            <label
                className={cx("input-label reference-field__title", {
                    "reference-field__title-error": meta && meta.error && meta.touched,
                })}
            >
                {title}
                {required && <span className="required-field">*</span>}
            </label>
            <div
                className={cx("reference-field__wrapper", {
                    "reference-field__wrapper-error": meta && meta.error && meta.touched,
                    "has-url-value": url,
                    "empty-url-value": !url || url === "",
                    "reference-focused": meta && meta.active,
                    "reference-disabled": disabled,
                })}
                onClick={() => {
                    if (disabled) return;
                    if (!url || url === "") {
                        handleEditClick();
                    }
                }}
            >
                <div className="reference-field__input-wrapper">
                    <a href={url} className="reference-field__link" target="_blank" rel="noopener noreferrer">
                        <ReactSVG
                            className="reference-field__icon reference-field__icon-chain"
                            src={`/data/svg/icon-chain.svg`}
                        />
                        <div className="reference-field__input">{!url ? placeholder : label}</div>
                    </a>
                </div>
                <div
                    className={cx("reference-field__icon reference-field__icon-edit-pen", {
                        "reference-field__icon-hidden": !url,
                    })}
                    onClick={handleEditClick}
                >
                    <ReactSVG className="" src={`/data/svg/icon-edit-pen.svg`} />
                </div>
            </div>
            {meta && meta.error && meta.touched && <div className="reference-field__error-message">{meta.error}</div>}

            {isEditing && (
                <SmartPortal
                    portalClassName="portal-reference-form-popup"
                    targetElement={widgetRef && widgetRef.current}
                    portalPlacement={portalPlacement}
                    portalKey={input.name}
                    onClickOutside={undoChanges}
                    repositionTrigger={repositionTrigger}
                    delayedRepositionTrigger={delayedRepositionTrigger}
                    delayedRepositionDelay={delayedRepositionDelay}
                >
                    <div className="reference-field__modal">
                        {isSaving && <Loader />}
                        <div className="reference-field__modal-wrapper">
                            <label className="input-label reference-field__modal-label">
                                {t("reference.url")}
                                <span className="required-field">*</span>
                            </label>
                            <textarea
                                type="text"
                                value={urlModal}
                                onChange={e => setUrlModal(e.target.value)}
                                className={cx("reference-field__modal-input reference-field__modal-input-textarea", {
                                    "reference-field__modal-input-error": urlModalError,
                                })}
                                maxLength={2000}
                            ></textarea>
                            {urlModalError && (
                                <div className="reference-field__error-message reference-field__modal-error">
                                    {urlModalError}
                                </div>
                            )}
                        </div>

                        <div className="reference-field__modal-wrapper">
                            <label className="input-label reference-field__modal-label">{t("reference.label")}</label>
                            <input
                                type="text"
                                value={labelModal}
                                onChange={e => setLabelModal(e.target.value)}
                                className="reference-field__modal-input"
                                maxLength={250}
                            />
                        </div>
                        <div className="reference-field__modal-buttons">
                            <button
                                title={t("reference.delete")}
                                className="reference-field__modal-button reference-field__modal-button-delete"
                                type="button"
                                onClick={handleDelete}
                            >
                                <ReactSVG className="reference-field__icon-bin" src={`/data/svg/icon-bin.svg`} />
                                <span>{t("reference.delete")}</span>
                            </button>
                            <button
                                title={t("save")}
                                className="reference-field__modal-button reference-field__modal-button-save"
                                type="button"
                                onClick={handleSave}
                            >
                                <ReactSVG className="reference-field__icon-check" src={`/data/svg/icon-check.svg`} />
                                <span>{t("save")}</span>
                            </button>
                        </div>
                    </div>
                    {/* </div> */}
                </SmartPortal>
            )}
        </div>
    );
};

export default Reference;
